/* eslint-disable ts/no-explicit-any */
import type { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios"

import { SuiteAdapter } from "@peerup/suite/adapter"
import { genId } from "@peerup/utils"
import axios from "axios"

import { envConfig } from "@/config"
import { HTTP_STATUS } from "@/enums/httpEnum"
import { i18n } from "@/plugins/i18n"
import { useUserStore } from "@/store/modules/user"
import { message as $message, message } from "@/utils/message"

import $logger, { ErrorAction } from "./logger"

// const REDIRECT_PAGE = `${window.location.origin}update/503.html`
const isRedirect = (response: AxiosResponse) => {
  if (!response) return false
  const data = response.data

  if (typeof data === "string") {
    return data.includes("<title>peerup-系统维护</title>")
  }
  return (
    response.status === HTTP_STATUS.REDIRECT || response.status === HTTP_STATUS.REDIRECT_TEMPORARY
  )
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  retry?: number
  /**
   * 是否忽略错误信息
   */
  noMsg?: boolean
  /**
   * 是否忽略鉴权
   */
  ignoreAuth?: boolean
}
type RequestConfig = InternalAxiosRequestConfig & CustomAxiosRequestConfig

$message.config({
  maxCount: 1,
})

// 在恢复后手动清除消息队列
window.addEventListener("focus", () => {
  $message.destroy()
})

export interface RequestOptions {
  /** 当前接口权限, 不需要鉴权的接口请忽略， 格式：sys:user:add */
  permCode?: string
  /** 是否直接获取data，而忽略message等 */
  isGetDataDirectly?: boolean
  /** 请求成功是提示信息 */
  successMsg?: string
  /** 请求失败是提示信息 */
  errorMsg?: string
  /** 是否mock数据请求 */
  isMock?: boolean
}

const UNKNOWN_ERROR = "未知错误，请重试"

const NO_ERROR_LIST = [41004]

export const baseApiUrl = envConfig.VITE_APP_DOMAIN_URL

const service = axios.create({
  baseURL: baseApiUrl,
  timeout: 60000 * 3, // 为了kernel启动 timeout改为3分钟,
  beforeRedirect: (_options, responseDetails) => {
    const url = responseDetails.headers.location || window.location.href
    $logger.log("离开页面", { url }, { immediate: true })
    window.location.href = url
  },
})

service.interceptors.request.use(
  async (config: RequestConfig) => {
    config.headers["X-Trace-Id"] = config.headers["X-Trace-Id"] || config.params?.request_id || genId()
    return config
  },
  error => {
    error.isAxiosError = true
    return Promise.reject(error)
  },
)

service.interceptors.response.use(
  response => {
    const res = response.data
    res.isAxiosError = true
    res.url = response.config?.url
    res.params = response.config?.params
    const userStore = useUserStore()
    if (res.code !== HTTP_STATUS.OK) {
      res.message = res.sub_msg || res.msg
      $logger.error(ErrorAction.XML_HTTP_REQUEST_ERROR, { error: res, url: response.config?.url, params: response.config?.params, traceId: response.config?.headers["X-Trace-Id"] })
      if (userStore.isMicroApp) {
        SuiteAdapter.current?.dispatch("fromChild", {
          tip: true,
          closeModal: false,
          msg: i18n.$t(res.i18nKey, { data: res.i18nData }, res.sub_msg || res.msg),
          successed: res.code === HTTP_STATUS.OK,
        })
      } else if (res.code === HTTP_STATUS.LOGIN_CHECK_FAIL) {
        $logger.log("离开页面", { url: "/accounts/login" }, { immediate: true })
        window.location.href = "/accounts/login"
      // token过期跳转到验证码登录页
      } else if (res.code === HTTP_STATUS.VERIFICATION_TOKEN_INVALID) {
        $message.error(i18n.$t(res.i18nKey, { data: res.i18nData }, res.sub_msg || res.msg))
        $logger.log("[accounts] token过期跳转到验证码登录页", { res })
        userStore.updateState({ showType: "init" })
      } else if (!NO_ERROR_LIST.includes(res.code)) {
        message.error(i18n.$t(res.i18nKey, { data: res.i18nData }, res.sub_msg || res.msg))
      }
      return Promise.reject(res)
    }
    return res
  },
  error => {
    if (error.name === "CanceledError") return Promise.reject(error) // 请求被取消
    const errMsg = error?.message ?? UNKNOWN_ERROR
    const userStore = useUserStore()
    if (!error.response) {
      if (errMsg !== "canceled") {
        $message.destroy("")
        !error.config?.noMsg && $message.error(i18n.$t(error.response?.data.i18nKey, { data: error.response.data.i18nData }, error.response.data.sub_msg || error.response.data.msg))
      }
      return Promise.reject(error)
    }
    // 处理 301 302 重定向
    if (isRedirect(error.response)) {
      const url = error.response.headers.location || window.location.href
      $logger.log("离开页面", { url }, { immediate: true })
      window.location.href = url
      return
    }
    if (error.response.status === HTTP_STATUS.UNAUTHORIZED) {
      $message.error(i18n.$t(error.response?.data.i18nKey, { data: error.response.data.i18nData }, error.response.data.sub_msg || error.response.data.msg || UNKNOWN_ERROR))
      if (userStore.isMicroApp) {
        SuiteAdapter.current?.dispatch("fromChild", {
          tip: false,
          closeModal: true,
        })
      } else {
        $logger.log("离开页面", { url: "/accounts/login" }, { immediate: true })
        window.location.href = "/accounts/login"
      }
    } else {
      if (error.response.status >= 500) {
        if (error.config?.retry) {
          error.config.retry -= 1
          return new Promise(resolve => {
            window.setTimeout(() => resolve(service(error.config)), 2000) // 有个前提条件isGetDataDirectly=false
          })
        }
        $message.destroy("")
        !error.config?.noMsg && $message.error("网络异常，请稍后再试")
      } else if (error.response.status === HTTP_STATUS.FORBIDDEN) { // 403错误码 之前不给提示 下面的是特殊处理需要提示
        if (error.response.data.code === HTTP_STATUS.IP_PERMISSION) {
          // 检查当前路径是否在白名单中,如果不在白名单中则登出
          $message.error(i18n.$t(error.response?.data.i18nKey, error.response.data.sub_msg || error.response.data.msg || "由于组织管理员设置，内网环境无法访问外部组织"))
          if (window.location.pathname.includes("/auth_redirect")) {
            return
          }
        // IP权限校验失败时--跳转兜底页
        } else if (error.response.data.code === HTTP_STATUS.IP_LIMIT_DATA) {
          $message.error(i18n.$t(error.response?.data.i18nKey, error.response.data.sub_msg || error.response.data.msg || UNKNOWN_ERROR))
        // IP权限校验失败时--切组织被拦截
        } else if (error.response.data.code === HTTP_STATUS.IP_LIMIT_OPERATE) {
          $message.error(i18n.$t(error.response?.data.i18nKey, error.response.data.sub_msg || error.response.data.msg || UNKNOWN_ERROR))
          error.response.data.isAxiosError = true
          return Promise.reject(error.response.data)
        } else if (error.response.data.code === HTTP_STATUS.NOTFOUND) {
          $message.error(i18n.$t(error.response?.data.i18nKey, error.response.data.sub_msg || error.response.data.msg || UNKNOWN_ERROR))
        }
      }
      // 网关层403、404不给提示
    }
    error.message = errMsg
    return Promise.reject(error)
  },
)

export type Response<T = any> = {
  [x: string]: unknown
  code: number
  message: string
  msg: string
  data: T
}

export type BaseResponse<T = any> = Promise<Response<T>>

export const request = async <T = any>(
  config: CustomAxiosRequestConfig,
  options?: RequestOptions,
): Promise<T> => {
  try {
    const { isGetDataDirectly = true } = options || {}
    const res = await service.request(config)

    return isGetDataDirectly ? res?.data : res
  } catch (error: any) {
    return Promise.reject(error)
  }
}
